<template>
  <div role="presentation">
    <div
      v-if="isLoadingReport"
      class="py-10 px-4"
    >
      <span
        class="flex flex-col mt-1"
      >
        <aha-skeleton-loader
          class="mb-1"
          width="90%"
          height="12px"
        />
        <aha-skeleton-loader
          class="mb-1"
          width="70%"
          height="12px"
        />
        <aha-skeleton-loader
          width="30%"
          height="12px"
        />
      </span>
    </div>
    <div
      v-if="feedbackListLength && !isLoadingReport"
      class="report-private-feedback"
    >
      <div v-if="totalRatings" class="box-ratting">
        <div class="rating">
          <div class="text-base-90 font-bold rating-avg">
            {{ avgRating }}
          </div>
          <div class="rating-content text-center">
            <StarRating
              class="flex justify-center"
              :controlled="false"
              :rate="Number(avgRating)"
              size="16px"
            />
            <div class="text-left text-xs text-base-80 mt-1">
              {{ totalRatings > 1
                ? $i18nPresenter.t('x ratings', { number: totalRatings })
                : $i18nPresenter.t('x rating', { number: totalRatings })
              }}
            </div>
          </div>
        </div> <!-- /.rating -->
      </div>
      <div v-if="feedbackListLength" class="box-feedback">
        <div
          v-for="(item) in feedbackList"
          :key="item.id"
          class="mb-3"
        >
          <FeedbackItemV2
            :feedbackGroup="item"
          />
        </div>
      </div>
    </div>
    <div
      v-if="!feedbackListLength && !isLoadingReport"
      class="text-center py-6 px-4"
    >
      <div class="inline-block mb-6">
        <img
          src="@/assets/images/report/feedback.svg"
          width="172"
          height="100"
          alt=""
        >
      </div>

      <div class="text-sm">
        {{ $i18nPresenter.t("Feedback from your participants will be shown here.") }}
      </div>
    </div>
  </div>
</template>

<script>
import FeedbackItemV2 from './FeedbackItemV2.vue'

export default {
  components: {
    FeedbackItemV2,
  },
  props: {
    isLoadingReport: {
      type: Boolean,
      default: false,
    },
    oldFeedbacks: {
      type: Array,
      default: null,
    },
    newFeedbacks: {
      type: Object,
      default: () => {},
    },
  },
  computed: {
    feedbackList() {
      const hasNewFeedbacks = this.newFeedbacks
        && (
          Object.prototype.hasOwnProperty.call(this.newFeedbacks, 'comments')
          || Object.prototype.hasOwnProperty.call(this.newFeedbacks, 'ratings')
        )
      const hasOldFeedbacks = this.oldFeedbacks && this.oldFeedbacks.length

      if (hasNewFeedbacks && hasOldFeedbacks) {
        return this.mergeFeedbackVersions(this.oldFeedbacks, this.newFeedbacks)
      }

      if (hasOldFeedbacks) return this.mergeFeedbackVersions(this.oldFeedbacks, {})

      if (hasNewFeedbacks) return this.mergeFeedbackVersions([], this.newFeedbacks)

      return []
    },
    feedbackListLength() {
      return (this.feedbackList && this.feedbackList.length) || 0
    },
    totalRatings() {
      let total = 0

      if (this.feedbackListLength) {
        for (let i = 0; i < this.feedbackListLength; i++) {
          if (this.feedbackList[i][0] && this.feedbackList[i][0].rating) {
            total++
          }
        }
      }

      return total
    },
    avgRating() {
      let avg = 0

      if (this.feedbackListLength) {
        let numberOfRatings = 0
        const totalRating = this.feedbackList.reduce((
          total, current,
        ) => {
          const rating = current[0].rating || 0

          if (rating) {
            numberOfRatings++
          }

          return total + rating
        }, 0)

        avg = numberOfRatings
          ? parseFloat(totalRating / numberOfRatings).toFixed(1)
          : parseFloat(totalRating).toFixed(1)
      }

      return avg
    },
    hasNoFeedback() {
      return this.feedbackList && !this.feedbackList.length
    },
  },
  methods: {
    _transformAudienceFeedbacksV1(oldFeedbackList) {
      const feedbacks = {}

      oldFeedbackList.forEach((feedback) => {
        if (Object.prototype.hasOwnProperty.call(feedbacks, `${feedback.audience}`)) {
          feedbacks[`${feedback.audience}`].push(feedback)
        } else {
          feedbacks[`${feedback.audience}`] = [feedback]
        }
      })

      let groupFeedbackWithDate = []

      // eslint-disable-next-line no-restricted-syntax, guard-for-in
      for (const property in feedbacks) {
        groupFeedbackWithDate.push({
          feedbacks: feedbacks[property],
          lastUpdatedAt: feedbacks[property][0].updatedAt,
        })
      }

      groupFeedbackWithDate = groupFeedbackWithDate.map((group) => {
        const { feedbacks: feedbackListByAudience, lastUpdatedAt } = group
        const groupFeedbacks = feedbackListByAudience.map((item) => {
          const rating = item.voteCount + 2 // old rating base [0..3]
          const newRating = rating > 5 ? 5 : rating // some old rating data greater than 3

          return {
            id: item.id,
            presentationId: item.presentationId,
            rating: newRating,
            comment: item.title,
            createdBy: null,
            createdAt: item.createdAt,
            updatedAt: item.updatedAt,
            audience: {
              id: null,
              email: item.email,
              audienceId: item.audience,
              emoji: item.audienceEmoji,
              name: item.audienceName,
              color: item.audienceColor,
            },
          }
        })

        return {
          feedbacks: groupFeedbacks,
          lastUpdatedAt,
        }
      })

      return groupFeedbackWithDate
    },

    _transformAudienceFeedbacksV2(newFeedbackList) {
      const {
        comments,
        ratings,
      } = newFeedbackList
      const feedbacks = {}

      comments.forEach((comment) => {
        const { audienceId } = comment.audience

        if (Object.prototype.hasOwnProperty.call(feedbacks, audienceId)) {
          feedbacks[audienceId].push(comment)
        } else {
          feedbacks[audienceId] = [comment]
        }
      })

      ratings.forEach((rating) => {
        const { audienceId } = rating.audience

        if (Object.prototype.hasOwnProperty.call(feedbacks, audienceId)) {
          feedbacks[audienceId] = feedbacks[audienceId].map((item) => ({
            ...item,
            rating: rating.rating,
          }))
        } else {
          feedbacks[audienceId] = [rating]
        }
      })

      // eslint-disable-next-line no-restricted-syntax, guard-for-in
      for (const property in feedbacks) {
        feedbacks[property] = feedbacks[property].reverse()
      }

      const groupFeedbackWithDate = []

      // eslint-disable-next-line no-restricted-syntax, guard-for-in
      for (const property in feedbacks) {
        groupFeedbackWithDate.push({
          feedbacks: feedbacks[property],
          lastUpdatedAt: feedbacks[property][0].updatedAt,
        })
      }

      return groupFeedbackWithDate
    },

    mergeFeedbackVersions(feedbackListV1 = [], feedbackListV2 = {}) {
      const listV1 = this._transformAudienceFeedbacksV1(feedbackListV1)
      const listV2 = this._transformAudienceFeedbacksV2(feedbackListV2)
      let mergedData = [
        ...listV1,
        ...listV2,
      ]

      mergedData = this._sortByDateAsc(mergedData)

      mergedData = this._flatFeedbackArray(mergedData)

      return mergedData
    },

    _flatFeedbackArray(list) {
      return list.map((listItem) => {
        const { feedbacks } = listItem

        return feedbacks.map((item) => ({
          createdAt: item.createdAt,
          updatedAt: item.updatedAt,
          comment: item.comment,
          rating: item.rating,
          color: item.audience.color,
          emoji: item.audience.emoji,
          name: item.audience.name,
          email: item.audience.email,
          isAnonymous: item.audience.isAnonymous,
        }))
      })
    },

    _sortByDateAsc(arr) {
      const newArr = [...arr]

      newArr.sort((a, b) => {
        return new Date(b.lastUpdatedAt).getTime() - new Date(a.lastUpdatedAt).getTime()
      })

      return newArr
    },
  },
}
</script>

<style lang="scss" scoped>
.report-private-feedback {
  margin-top: 20px;
}

.box-ratting {
  background-color: #F0F4FA;
  padding: 12px;
  border-radius: 4px;
  display: flex;
  justify-content: center;
}

.rating {
  display: flex;
  align-items: center;
  position: relative;

  &-content {
    position: relative;
    max-width: 250px;
    margin-left: auto;
    margin-right: auto;
  }

  &-avg {
    font-size: 40px;
    line-height: 54px;
    margin-right: 8px;
    color: #313131;
  }
}

.box-feedback {
  @apply my-5;

  &.over-box {
    padding-right: 10px;
  }
}
</style>
